import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import ProfileService from "service/ProfileService";
import { updateProfile } from "action/AuthAction";
import { encryptPassword } from "helpers/EncryptPassword";

const ChangePassword: React.FC = () => {
  const { profile } = useSelector((state: any) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const profileService = ProfileService();

  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const token = profile?.data?.token;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!oldPassword || !newPassword || !confirmPassword) {
      setError("Semua field harus diisi");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Password baru dan konfirmasi password tidak sama");
      return;
    }

    if (newPassword.length < 6) {
      setError("Password baru minimal 6 karakter");
      return;
    }

    if (!token) {
      setError("Token tidak ditemukan. Silakan login kembali.");
      return;
    }

    setLoading(true);
    try {
      const encryptedOldPassword = encryptPassword(oldPassword);
      const encryptedNewPassword = encryptPassword(newPassword);
      const encryptedConfirmPassword = encryptPassword(confirmPassword);

      const res = await profileService.changePassword(
        token,
        encryptedOldPassword, 
        encryptedNewPassword, 
        encryptedConfirmPassword
      );
      
      const responseData = res.data;
      
      if (!responseData) throw new Error("Data kosong dari server");

      if (responseData.token) {
        dispatch(updateProfile({
          ...profile.data,
          token: responseData.token,
        }));
        
        localStorage.setItem('token', responseData.token);
      }

      setSuccess("Password berhasil diubah!");
      
      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
      
      setTimeout(() => {
        navigate(-1);
      }, 2000);

    } catch (err: any) {
      console.error("Error details:", {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
      
      if (err.response?.data?.message?.includes("decrypt")) {
        setError("Password lama salah atau format tidak sesuai");
      } else if (err.response?.status === 401) {
        setError("Token tidak valid. Silakan login kembali.");
      } else {
        setError(err?.message || "Gagal mengubah password");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-wrapper">
      <div className="row">
        <div className="col-12">
          <div className="d-flex">
            <h5 style={{ flex: 1 }}>Change Password</h5>
          </div>
        </div>

        <div className="col-12">
          <div className="p-4" style={{ background: "white", borderRadius: "6px" }}>
            <form onSubmit={handleSubmit}>
              <div style={{ maxWidth: "500px" }}>
                <div className="mb-4">
                  <label className="form-label fw-bold">Old Password</label>
                  <input
                    type="password"
                    className="form-control"
                    value={oldPassword}
                    onChange={(e) => {
                      setOldPassword(e.target.value);
                      setError(null);
                      setSuccess(null);
                    }}
                    required
                    disabled={loading}
                    autoComplete="current-password"
                  />
                </div>

                <div className="mb-4">
                  <label className="form-label fw-bold">New Password</label>
                  <input
                    type="password"
                    className="form-control"
                    value={newPassword}
                    onChange={(e) => {
                      setNewPassword(e.target.value);
                      setError(null);
                      setSuccess(null);
                    }}
                    required
                    disabled={loading}
                    autoComplete="new-password"
                  />
                </div>

                <div className="mb-5">
                  <label className="form-label fw-bold">New Password Confirm</label>
                  <input
                    type="password"
                    className="form-control"
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      setError(null);
                      setSuccess(null);
                    }}
                    required
                    disabled={loading}
                    autoComplete="new-password"
                  />
                </div>

                {error && <div className="alert alert-danger">{error}</div>}
                {success && <div className="alert alert-success">{success}</div>}
              </div>
              
              <div className="d-flex justify-content-end mt-4">
                <button
                  type="button"
                  className="btn btn-outline-primary me-2 px-4"
                  onClick={() => navigate(-1)}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary px-4" 
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Changing Password...
                    </>
                  ) : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;